/**
 * Purchase Contract Configuration
 *
 * Comprehensive configuration for purchase contract forms.
 * This configuration replaces the hardcoded AppleStylePurchaseContractForm
 * with a declarative, maintainable structure.
 *
 * @module configs/contracts/purchaseContract
 */

import { ContractFormConfig, FieldType, FieldConfig } from '../../types/contractForm.types';

/**
 * Purchase Contract Form Configuration
 *
 * Defines the complete structure for purchase contract forms including:
 * - 7 steps: Contract Type, Seller Info, Buyer Info, Item Details, Payment, Additional Terms, Review
 * - 50+ fields with full validation
 * - Conditional logic for vehicle, real estate, and other specialized types
 * - RAG integration for intelligent text field suggestions
 *
 * @constant
 */
export const purchaseContractConfig: ContractFormConfig = {
  type: 'purchase',
  subtype: undefined,

  steps: [
    // ==================== STEP 0: Contract Type ====================
    {
      title: 'Typ smlouvy',
      subtitle: 'Vyberte, co chcete koupit/prodat',
      description: 'Výběr typu určí, jaká specifická pole budou v dalších krocích zobrazena',
      icon: 'Description',
      color: 'primary',
      fields: [
        {
          name: 'contract_type',
          label: 'Hlavní typ předmětu koupě',
          type: FieldType.SELECT,
          validation: [
            { type: 'required', message: 'Typ smlouvy je povinný' },
          ],
          gridProps: { xs: 12 },
          options: [
            { value: 'movita_vec', label: 'Movitá věc (nábytek, elektronika, oblečení)', icon: '🪑' },
            { value: 'nemovitost', label: 'Nemovitost (dům, byt, pozemek)', icon: '🏠' },
            { value: 'vozidlo', label: 'Vozidlo (auto, motorka, přívěs)', icon: '🚗' },
            { value: 'podil', label: 'Obchodní podíl ve společnosti', icon: '💼' },
            { value: 'cenne_papiry', label: 'Cenné papíry (akcie, dluhopisy)', icon: '📈' },
            { value: 'prava', label: 'Práva a pohledávky', icon: '⚖️' },
            { value: 'zvirata', label: 'Zvířata', icon: '🐕' },
            { value: 'sberatelske', label: 'Sběratelské předměty (umění, mince)', icon: '🎨' },
            { value: 'digitalni', label: 'Digitální aktiva (software, domény)', icon: '💻' },
          ],
        },
        {
          name: 'contract_subtype',
          label: 'Upřesnění typu',
          type: FieldType.SELECT,
          validation: [
            { type: 'required', message: 'Upřesnění typu je povinné' },
          ],
          gridProps: { xs: 12 },
          conditional: {
            field: 'contract_type',
            operator: 'notEquals',
            value: '',
            showWhenTrue: true,
          },
          // Options will be dynamically populated based on contract_type
          // This is handled by the form component
          options: [],
        },
      ],
    },

    // ==================== STEP 1: Seller Information ====================
    {
      title: 'Kdo prodává?',
      subtitle: 'Zadejte údaje o prodávajícím',
      icon: 'Business',
      color: 'primary',
      fields: [
        {
          name: 'seller_name',
          label: 'Jméno a příjmení / Název firmy',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Jméno prodávajícího je povinné' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'např. Jan Novák nebo ABC s.r.o.',
          helpText: 'Zadejte celé jméno fyzické osoby nebo název firmy',
          examples: ['Jan Novák', 'Marie Svobodová', 'TechSoft s.r.o.', 'AutoCZ a.s.'],
        },
        {
          name: 'seller_address',
          label: 'Adresa',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [
            { type: 'required', message: 'Adresa prodávajícího je povinná' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Ulice a číslo, PSČ Město',
          examples: [
            'Václavské náměstí 1, 110 00 Praha 1',
            'Náměstí Svobody 10, 602 00 Brno',
            'Hlavní 25, 301 00 Plzeň',
          ],
        },
        {
          name: 'seller_id',
          label: 'Rodné číslo / IČO',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Rodné číslo nebo IČO je povinné' },
            {
              type: 'pattern',
              value: /^(\d{6}\/?\d{4}|\d{8})$/,
              message: 'Neplatný formát rodného čísla nebo IČO',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '123456/7890 nebo 12345678',
          helpText: 'Rodné číslo pro fyzické osoby, IČO pro firmy',
          examples: ['123456/7890', '12345678'],
        },
        {
          name: 'seller_email',
          label: 'E-mail (volitelné)',
          type: FieldType.EMAIL,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'jmeno@email.cz',
          helpText: 'E-mail pro komunikaci ohledně smlouvy',
        },
        {
          name: 'seller_business_id',
          label: 'DIČ (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'CZ12345678',
          helpText: 'Daňové identifikační číslo pro firmy',
        },
      ],
    },

    // ==================== STEP 2: Buyer Information ====================
    {
      title: 'Kdo kupuje?',
      subtitle: 'Zadejte údaje o kupujícím',
      icon: 'Person',
      color: 'success',
      fields: [
        {
          name: 'buyer_name',
          label: 'Jméno a příjmení / Název firmy',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Jméno kupujícího je povinné' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'např. Petra Nováková nebo XYZ s.r.o.',
          examples: ['Petra Nováková', 'Tomáš Dvořák', 'Nákupy s.r.o.', 'Retail a.s.'],
        },
        {
          name: 'buyer_address',
          label: 'Adresa',
          type: FieldType.MULTILINE,
          rows: 2,
          validation: [
            { type: 'required', message: 'Adresa kupujícího je povinná' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Ulice a číslo, PSČ Město',
          examples: [
            'Wenceslas Square 1, 110 00 Prague 1',
            'Masaryk Street 15, 602 00 Brno',
          ],
        },
        {
          name: 'buyer_id',
          label: 'Rodné číslo / IČO',
          type: FieldType.TEXT,
          validation: [
            { type: 'required', message: 'Rodné číslo nebo IČO je povinné' },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '123456/7890 nebo 12345678',
        },
        {
          name: 'buyer_email',
          label: 'E-mail (volitelné)',
          type: FieldType.EMAIL,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'jmeno@email.cz',
        },
        {
          name: 'buyer_business_id',
          label: 'DIČ (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'CZ12345678',
          helpText: 'Daňové identifikační číslo pro firmy',
        },
      ],
    },

    // ==================== STEP 3: Item Details ====================
    {
      title: 'Co se prodává?',
      subtitle: 'Popište prodávanou věc',
      icon: 'ShoppingCart',
      color: 'warning',
      fields: [
        {
          name: 'item_description',
          label: 'Popis předmětu',
          type: FieldType.MULTILINE,
          rows: 3,
          validation: [
            { type: 'required', message: 'Popis věci je povinný' },
          ],
          gridProps: { xs: 12 },
          placeholder: 'Detailně popište co se prodává...',
          helpText: 'Čím detailnější popis, tím lépe chráněny budou vaše práva',
        },
        {
          name: 'item_category',
          label: 'Kategorie (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. Elektronika, Nábytek',
        },
        {
          name: 'item_condition',
          label: 'Stav věci',
          type: FieldType.SELECT,
          gridProps: { xs: 12, md: 6 },
          options: [
            { value: 'Nový', label: 'Nový', icon: '✨' },
            { value: 'Velmi dobrý', label: 'Velmi dobrý', icon: '😊' },
            { value: 'Dobrý', label: 'Dobrý', icon: '👍' },
            { value: 'Použitý', label: 'Použitý', icon: '🔄' },
            { value: 'Vystavní kus', label: 'Vystavní kus', icon: '🏪' },
            { value: 'Na náhradní díly', label: 'Na náhradní díly', icon: '🔧' },
          ],
        },
        {
          name: 'serial_number',
          label: 'Výrobní/sériové číslo (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. ABC123456789',
          helpText: 'Pomáhá při identifikaci věci a záručních nároků',
          // Hide for animals – those use evidenční/čip number instead
          conditional: {
            field: 'contract_type',
            operator: 'notEquals',
            value: 'zvirata',
            showWhenTrue: true,
          },
        },
        {
          name: 'animal_registration_number',
          label: 'Evidenční číslo (čip) – volitelné',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. číslo čipu nebo evidenční číslo',
          helpText: 'Identifikace zvířete (číslo čipu nebo evidenční číslo registru)',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'zvirata',
            showWhenTrue: true,
          },
        },
        {
          name: 'warranty_info',
          label: 'Informace o záruce (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12 },
          placeholder: 'např. Záruka do 31.12.2025, pozor na mechanické poškození',
          examples: [
            'Záruka výrobce 24 měsíců od zakoupení',
            'Záruka již vypršela',
            'Rozšířená záruka do 31.12.2025',
          ],
        },

        // ---------- VEHICLE-SPECIFIC FIELDS ----------
        {
          name: 'vehicle_vin',
          label: 'VIN kód vozidla',
          type: FieldType.TEXT,
          validation: [
            {
              type: 'required',
              message: 'VIN je povinný pro vozidla',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. WAUZZZ4G7EN123456',
          helpText: '17místný identifikační kód vozidla',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'vozidlo',
            showWhenTrue: true,
          },
        },
        {
          name: 'vehicle_registration',
          label: 'SPZ (registrační značka)',
          type: FieldType.TEXT,
          validation: [
            {
              type: 'required',
              message: 'SPZ je povinná',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. 1A2 3456',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'vozidlo',
            showWhenTrue: true,
          },
        },
        {
          name: 'vehicle_mileage',
          label: 'Počet najetých kilometrů',
          type: FieldType.NUMBER,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. 120000',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'vozidlo',
            showWhenTrue: true,
          },
        },
        {
          name: 'vehicle_year',
          label: 'Rok výroby',
          type: FieldType.NUMBER,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. 2018',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'vozidlo',
            showWhenTrue: true,
          },
        },
        {
          name: 'technical_inspection',
          label: 'Platnost STK/emisí',
          type: FieldType.DATE,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Datum platnosti technické kontroly',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'vozidlo',
            showWhenTrue: true,
          },
        },

        // ---------- REAL ESTATE-SPECIFIC FIELDS ----------
        {
          name: 'cadastral_number',
          label: 'Katastrální číslo',
          type: FieldType.TEXT,
          validation: [
            {
              type: 'required',
              message: 'Katastrální číslo je povinné',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. 1234/5',
          helpText: 'Číslo parcely v katastru nemovitostí',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'nemovitost',
            showWhenTrue: true,
          },
        },
        {
          name: 'land_registry',
          label: 'List vlastnictví',
          type: FieldType.TEXT,
          validation: [
            {
              type: 'required',
              message: 'List vlastnictví je povinný',
            },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. LV 1234',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'nemovitost',
            showWhenTrue: true,
          },
        },
        {
          name: 'property_size',
          label: 'Výměra (m²)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. 85 m²',
          helpText: 'Celková podlahová plocha',
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'nemovitost',
            showWhenTrue: true,
          },
        },
        {
          name: 'energy_certificate',
          label: 'Energetický štítek',
          type: FieldType.SELECT,
          gridProps: { xs: 12, md: 6 },
          options: [
            { value: 'A', label: 'A - Velmi úsporná' },
            { value: 'B', label: 'B - Úsporná' },
            { value: 'C', label: 'C - Vyhovující' },
            { value: 'D', label: 'D - Méně vyhovující' },
            { value: 'E', label: 'E - Nevyhovující' },
            { value: 'F', label: 'F - Velmi nevyhovující' },
            { value: 'G', label: 'G - Mimořádně nehospodárná' },
          ],
          conditional: {
            field: 'contract_type',
            operator: 'equals',
            value: 'nemovitost',
            showWhenTrue: true,
          },
        },
      ],
    },

    // ==================== STEP 4: Price and Payment ====================
    {
      title: 'Za kolik a jak?',
      subtitle: 'Finanční podmínky prodeje',
      icon: 'Payment',
      color: 'error',
      fields: [
        {
          name: 'price',
          label: 'Kupní cena',
          type: FieldType.NUMBER,
          validation: [
            { type: 'required', message: 'Cena je povinná' },
            { type: 'min', value: 1, message: 'Cena musí být větší než 0' },
          ],
          gridProps: { xs: 12, md: 6 },
          placeholder: '50000',
          helpText: 'Zadejte částku v českých korunách',
          examples: ['5000', '50000', '500000'],
        },
        {
          name: 'payment_method',
          label: 'Způsob platby',
          type: FieldType.SELECT,
          validation: [
            { type: 'required', message: 'Způsob platby je povinný' },
          ],
          gridProps: { xs: 12, md: 6 },
          options: [
            { value: 'Hotově', label: 'Hotově při předání', icon: '💰' },
            { value: 'Bankovním převodem', label: 'Bankovním převodem', icon: '🏦' },
            { value: 'Kartou', label: 'Platební kartou', icon: '💳' },
            { value: 'PayPal', label: 'PayPal', icon: '📱' },
            { value: 'Kombinace', label: 'Kombinace způsobů', icon: '🔄' },
          ],
        },
        {
          name: 'payment_terms',
          label: 'Podrobnosti platby',
          type: FieldType.MULTILINE,
          rows: 2,
          gridProps: { xs: 12 },
          placeholder: 'např. 50% záloha hotově, zbytek bankovním převodem do 7 dnů',
          examples: [
            '50% záloha hotově, zbytek bankovním převodem do 7 dnů',
            '10 000 Kč záloha, zbytek při převzetí',
          ],
          conditional: {
            field: 'payment_method',
            operator: 'equals',
            value: 'Kombinace',
            showWhenTrue: true,
          },
        },
      ],
    },

    // ==================== STEP 5: Additional Terms ====================
    {
      title: 'Dodatečné podmínky',
      subtitle: 'Volitelné informace a zvláštní podmínky',
      icon: 'Description',
      color: 'secondary',
      fields: [
        {
          name: 'delivery_date',
          label: 'Datum předání (volitelné)',
          type: FieldType.DATE,
          gridProps: { xs: 12, md: 6 },
          helpText: 'Kdy se uskuteční předání věci',
        },
        {
          name: 'delivery_location',
          label: 'Místo předání (volitelné)',
          type: FieldType.TEXT,
          gridProps: { xs: 12, md: 6 },
          placeholder: 'např. u prodávajícího, v Praze',
          examples: [
            'U prodávajícího (viz adresa výše)',
            'Praha, náměstí Míru',
            'Brno, hlavní nádraží',
          ],
        },
        {
          name: 'special_conditions',
          label: 'Zvláštní podmínky (volitelné)',
          type: FieldType.MULTILINE,
          rows: 3,
          gridProps: { xs: 12 },
          placeholder: 'Zde můžete uvést jakékoli dodatečné podmínky...',
          examples: [
            'Kupující si věc prohlédl a bere ji v současném stavu',
            'Věc bude předána včetně originálního balení a příslušenství',
            'Prodávající poskytuje instruktáž k použití',
          ],
          helpText: 'Například podmínky předání, dodatečné záruky, apod.',
        },
      ],
    },

    // Review step removed – preview is opened automatically in UI
  ],

  // ==================== RAG Configuration ====================
  rag: {
    enabled: true,
    fields: [
      'item_description',
      'special_conditions',
      'warranty_info',
      'payment_terms',
    ],
    promptTemplate: `Analyze this purchase contract data and provide intelligent suggestions for:
1. Item description completeness
2. Legal compliance and required clauses
3. Payment terms clarity
4. Risk mitigation recommendations`,
  },

  // ==================== Custom Validation ====================
  customValidation: (formData: any) => {
    const errors: Record<string, string> = {};

    // Validate cash payments over 270,000 CZK
    if (
      formData.payment_method === 'Hotově' &&
      formData.price > 270000
    ) {
      errors.price =
        'Hotovostní platby nad 270 000 Kč musí být nahlášeny finančním úřadem';
    }

    // Ensure vehicle fields are present for vehicle contracts
    if (formData.contract_type === 'vozidlo') {
      if (!formData.vehicle_vin) {
        errors.vehicle_vin = 'VIN je povinný pro vozidla';
      }
      if (!formData.vehicle_registration) {
        errors.vehicle_registration = 'SPZ je povinná pro vozidla';
      }
    }

    // Ensure real estate fields are present
    if (formData.contract_type === 'nemovitost') {
      if (!formData.cadastral_number) {
        errors.cadastral_number = 'Katastrální číslo je povinné pro nemovitosti';
      }
      if (!formData.land_registry) {
        errors.land_registry = 'List vlastnictví je povinný pro nemovitosti';
      }
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors: Object.keys(errors).length > 0 ? errors : undefined,
    };
  },

  // ==================== Data Transformation ====================
  transformData: (formData: any) => {
    // Transform data before submission if needed
    // For example, format dates, normalize phone numbers, etc.
    const transformed = { ...formData };

    // Ensure price is a number
    if (transformed.price) {
      transformed.price = Number(transformed.price);
    }

    // Format dates to ISO string
    if (transformed.delivery_date) {
      transformed.delivery_date = new Date(transformed.delivery_date).toISOString();
    }

    return transformed;
  },
};

/**
 * Helper function to get subtype options based on contract type
 *
 * @param contractType - The main contract type
 * @returns Array of select options for the subtype field
 */
export const getSubtypeOptions = (contractType: string) => {
  const subtypeMap: Record<string, Array<{ value: string; label: string; icon?: string }>> = {
    movita_vec: [
      { value: 'elektronika', label: 'Elektronika a technika', icon: '💻' },
      { value: 'nabytek', label: 'Nábytek a vybavení', icon: '🪑' },
      { value: 'obleceni', label: 'Oblečení a obuv', icon: '👕' },
      { value: 'sportovni', label: 'Sportovní vybavení', icon: '⚽' },
      { value: 'nastroje', label: 'Nářadí a nástroje', icon: '🔧' },
      { value: 'ostatni', label: 'Ostatní movité věci', icon: '📦' },
    ],
    nemovitost: [
      { value: 'byt', label: 'Byt v osobním vlastnictví', icon: '🏢' },
      { value: 'dum', label: 'Rodinný dům', icon: '🏠' },
      { value: 'pozemek', label: 'Pozemek', icon: '🌳' },
      { value: 'chata', label: 'Chata/chalupa', icon: '🏡' },
      { value: 'garaz', label: 'Garáž/garážové stání', icon: '🚗' },
      { value: 'komercni', label: 'Komerční nemovitost', icon: '🏪' },
    ],
    vozidlo: [
      { value: 'auto', label: 'Osobní automobil', icon: '🚗' },
      { value: 'motorka', label: 'Motocykl', icon: '🏍️' },
      { value: 'nakladni', label: 'Nákladní vozidlo', icon: '🚚' },
      { value: 'prives', label: 'Přívěs/návěs', icon: '🚐' },
      { value: 'lod', label: 'Loď/člun', icon: '⛵' },
      { value: 'specialni', label: 'Speciální vozidlo', icon: '🚜' },
    ],
    zvirata: [
      { value: 'pes', label: 'Pes', icon: '🐕' },
      { value: 'kocka', label: 'Kočka', icon: '🐈' },
      { value: 'kun', label: 'Kůň', icon: '🐎' },
      { value: 'hospodarsky', label: 'Hospodářské zvíře', icon: '🐄' },
      { value: 'exoticke', label: 'Exotické zvíře', icon: '🦜' },
      { value: 'ostatni_zvirata', label: 'Ostatní zvířata', icon: '🐾' },
    ],
  };

  return subtypeMap[contractType] || [{ value: 'obecny', label: 'Obecný typ', icon: '📝' }];
};

/**
 * Helper function to get step fields for validation
 *
 * @param stepIndex - The step index (0-based)
 * @returns Array of field names for the given step
 */
export const getStepFields = (stepIndex: number): string[] => {
  if (stepIndex < 0 || stepIndex >= purchaseContractConfig.steps.length) {
    return [];
  }

  return purchaseContractConfig.steps[stepIndex].fields
    .filter((field: FieldConfig) => field.validation?.some((rule: any) => rule.type === 'required'))
    .map((field: FieldConfig) => field.name);
};

export default purchaseContractConfig;
